import React from 'react';
import { Box, BoxProps, useMantineColorScheme } from '@mantine/core';

interface IconWrapperProps extends BoxProps {
    size: number;
    children: React.ReactNode;
}

export const IconWrapper: React.FC<IconWrapperProps> = ({ size, children, style, ...props }) => {
    const { colorScheme } = useMantineColorScheme();
    
    // For very small icons (e.g. in trees), keep it simple but consistent
    if (size < 20) {
        return (
            <Box
                w={size}
                h={size}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    ...style
                }}
                {...props}
            >
                {/* Scale slightly up for visibility in trees */}
                {React.isValidElement(children) 
                    ? React.cloneElement(children as React.ReactElement<any>, { 
                        size: (children.props.size || size) * 0.9,
                        color: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                      }) 
                    : children
                }
            </Box>
        );
    }

    return (
        <Box
            w={size}
            h={size}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                borderRadius: Math.max(8, size * 0.25),
                border: colorScheme === 'dark' 
                    ? '1px solid rgba(255, 255, 255, 0.12)' 
                    : '1px solid rgba(0, 0, 0, 0.08)',
                background: colorScheme === 'dark'
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                boxShadow: colorScheme === 'dark' 
                    ? '0 4px 12px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.05)' 
                    : '0 4px 12px rgba(0,0,0,0.05), inset 0 1px 0 #ffffff',
                ...style
            }}
            {...props}
        >
            {/* Scale child icon by 0.75-0.8 to make it BIGGER inside the background */}
            {React.isValidElement(children) 
                ? React.cloneElement(children as React.ReactElement<any>, { 
                    size: (children.props.size || size) * 0.75,
                    color: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'
                  }) 
                : children
            }
        </Box>
    );
};
